import { energyAnalysisApiSlice as api } from '../../../apiSlice';
export const addTagTypes = ['CO2e Coefficients for Utility Providers'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGet:
        build.query<
          GetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetApiResponse,
          GetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetApiArg
        >({
          query: (queryArg) => ({
            url: `/configurable_utility_providers/${queryArg.utilityType}`,
          }),
          providesTags: ['CO2e Coefficients for Utility Providers'],
        }),
      getUtilityProvidersUtilityProvidersUtilityTypeGet: build.query<
        GetUtilityProvidersUtilityProvidersUtilityTypeGetApiResponse,
        GetUtilityProvidersUtilityProvidersUtilityTypeGetApiArg
      >({
        query: (queryArg) => ({
          url: `/utility_providers/${queryArg.utilityType}`,
        }),
        providesTags: ['CO2e Coefficients for Utility Providers'],
      }),
      setUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypePut:
        build.mutation<
          SetUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypePutApiResponse,
          SetUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypePutApiArg
        >({
          query: (queryArg) => ({
            url: `/utility_provider_coefficient/${queryArg.utilityType}`,
            method: 'PUT',
            body: queryArg.utilityProviderCoefficientInput,
          }),
          invalidatesTags: ['CO2e Coefficients for Utility Providers'],
        }),
      getUtilityProviderCoefficientsUtilityProviderCoefficientsUtilityTypeProviderIdGet:
        build.query<
          GetUtilityProviderCoefficientsUtilityProviderCoefficientsUtilityTypeProviderIdGetApiResponse,
          GetUtilityProviderCoefficientsUtilityProviderCoefficientsUtilityTypeProviderIdGetApiArg
        >({
          query: (queryArg) => ({
            url: `/utility_provider_coefficients/${queryArg.utilityType}/${queryArg.providerId}`,
          }),
          providesTags: ['CO2e Coefficients for Utility Providers'],
        }),
      deleteUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypeProviderIdCoefficientIdDelete:
        build.mutation<
          DeleteUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypeProviderIdCoefficientIdDeleteApiResponse,
          DeleteUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypeProviderIdCoefficientIdDeleteApiArg
        >({
          query: (queryArg) => ({
            url: `/utility_provider_coefficient/${queryArg.utilityType}/${queryArg.providerId}/${queryArg.coefficientId}`,
            method: 'DELETE',
          }),
          invalidatesTags: ['CO2e Coefficients for Utility Providers'],
        }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as energyAnalysisOpenApi };
export type GetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetApiResponse =
  /** status 200 Successful Response */ UtilityProvidersList;
export type GetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetApiArg =
  {
    /** The type of utility provider: 'heat' or 'water' */
    utilityType: AnalysisType;
  };
export type GetUtilityProvidersUtilityProvidersUtilityTypeGetApiResponse =
  /** status 200 Successful Response */ UtilityProvidersList;
export type GetUtilityProvidersUtilityProvidersUtilityTypeGetApiArg = {
  /** The type of utility provider: 'heat' or 'water' */
  utilityType: AnalysisType;
};
export type SetUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypePutApiResponse =
  /** status 200 Successful Response */ UtilityProviderCoefficientOutput;
export type SetUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypePutApiArg =
  {
    /** The type of utility provider: 'heat' or 'water' */
    utilityType: AnalysisType;
    utilityProviderCoefficientInput: UtilityProviderCoefficientInput;
  };
export type GetUtilityProviderCoefficientsUtilityProviderCoefficientsUtilityTypeProviderIdGetApiResponse =
  /** status 200 Successful Response */ UtilityProviderCoefficientHistory;
export type GetUtilityProviderCoefficientsUtilityProviderCoefficientsUtilityTypeProviderIdGetApiArg =
  {
    /** The type of utility provider: 'heat' or 'water' */
    utilityType: AnalysisType;
    /** The unique 4-digit provider identifier */
    providerId: string;
  };
export type DeleteUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypeProviderIdCoefficientIdDeleteApiResponse =
  /** status 200 Successful Response */ UtilityProviderCoefficientDeleteOutput;
export type DeleteUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypeProviderIdCoefficientIdDeleteApiArg =
  {
    /** The type of utility provider: 'heat' or 'water' */
    utilityType: AnalysisType;
    /** The unique 4-digit provider identifier */
    providerId: string;
    /** The coefficient ID (unique within this provider) */
    coefficientId: number;
  };
export type UtilityProvider = {
  /** Unique 4-digit provider identifier */
  provider_id: string;
  /** Name of the utility provider */
  provider_name: string;
};
export type UtilityProvidersList = {
  /** List of all utility providers */
  providers: UtilityProvider[];
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export type AnalysisType = 'power' | 'heat' | 'water';
export type UtilityProviderCoefficientOutput = {
  /** CO2e coefficient: kg CO2e per m³ for water, kg CO2e per kWh for heat */
  co2e_coefficient_per_unit: number;
  /** ISO 8601 timestamp when coefficient becomes active. Remains active until the next coefficient's start_time. */
  start_time: string;
  /** ISO 8601 timestamp when this entry was last modified */
  last_updated: string;
  /** Unique ID of the coefficient entry - ID is unique per provider (not globally) */
  coefficient_id: number;
  /** Unique 4-digit provider identifier */
  provider_id: string;
  /** Name of the utility provider */
  provider_name: string;
};
export type UtilityProviderCoefficientInput = {
  /** Unique 4-digit provider identifier (1000+ for water, 5000+ for heat) */
  provider_id: string;
  /** CO2e coefficient: kg CO2e per m³ for water, kg CO2e per kWh for heat */
  co2e_coefficient_per_unit: number;
  /** ISO 8601 timestamp when this coefficient becomes active. Format: YYYY-MM-DDTHH:MM:SS+0000. The coefficient remains active until the next coefficient's start_time. */
  start_time: string;
};
export type UtilityProviderCoefficientModel = {
  /** CO2e coefficient: kg CO2e per m³ for water, kg CO2e per kWh for heat */
  co2e_coefficient_per_unit: number;
  /** ISO 8601 timestamp when coefficient becomes active. Remains active until the next coefficient's start_time. */
  start_time: string;
  /** ISO 8601 timestamp when this entry was last modified */
  last_updated: string;
  /** Unique ID of the coefficient entry - ID is unique per provider (not globally) */
  coefficient_id: number;
};
export type UtilityProviderCoefficientHistory = {
  /** Unique 4-digit provider identifier */
  provider_id: string;
  /** Name of the utility provider */
  provider_name: string;
  /** List of all coefficients for this provider, ordered by start_time */
  coefficients: UtilityProviderCoefficientModel[];
};
export type UtilityProviderCoefficientDeleteOutput = {
  /** Success message */
  message: string;
  /** Unique 4-digit provider identifier */
  provider_id: string;
  /** Name of the utility provider */
  provider_name: string;
  /** ID of the deleted coefficient */
  coefficient_id: number;
  /** Start time of the deleted coefficient */
  deleted_start_time: string;
};
export const {
  useGetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetQuery,
  useGetUtilityProvidersUtilityProvidersUtilityTypeGetQuery,
  useSetUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypePutMutation,
  useGetUtilityProviderCoefficientsUtilityProviderCoefficientsUtilityTypeProviderIdGetQuery,
  useDeleteUtilityProviderCoefficientUtilityProviderCoefficientUtilityTypeProviderIdCoefficientIdDeleteMutation,
} = injectedRtkApi;
